/* eslint-disable @typescript-eslint/no-explicit-any */

import { TFunction } from 'next-i18next'
import { getCookie } from 'cookies-next'
import { getWorkingAreaName } from '@/utils/getUserWorkingArea'
import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'

type Config = {
	apiKey: string
	product: string
}

const getColorScheme = (): string => {
	if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
		return 'dark'
	}

	if (window.matchMedia('(prefers-color-scheme: light)').matches) {
		return 'light'
	}

	return 'none'
}

const getThemeFromCookie = (): string => {
	const theme = getCookie('theme')
	return theme ? theme.toString().toLowerCase() : 'none'
}

export default function CookieControlConfig(t: TFunction, user: User): Config {
	const config = {
		apiKey: 'a185aedb922478490fc5bbbeba3a7b4f6eaabde4',
		product: 'PRO_MULTISITE',
		initialState: 'notify',
		ccpaConfig: {
			description:
				'This may include the website or our third-party tools processing personal data. ' +
				'In the case of your personal data, you may opt out of such activity by using the buttons below. ' +
				'To find out more about the categories of personal information collected and the purposes for which ' +
				'such information will be used, please refer to our',
			name: 'Privacy & Cookies Policy',
			url: '/privacy',
			updated: '15/11/2021',
		},
		rejectButton: false,
		theme: 'light',
		closeStyle: 'button',
		notifyDismissButton: false,
		settingsStyle: 'button',
		necessaryCookies: [
			'.ASPXAUTH',
			'.auth',
			'ASP.NET_SessionId',
			'__currency',
			'anwPlayerVolume',
			'AWSALB',
			'AWSALBCORS',
			'CartOpenedFromUrl',
			'CartToken',
			'ip-address-override',
			'liveagent_oref',
			'liveagent_ptid',
			'liveagent_sid',
			'liveagent_vc',
			'changeLocationPopupDismissed',
			'cntShopping',
			'previewList',
			'siteLanguage',
			'theme',
			'toggles',
			'UsageType',
			'upsellCreator',
			'user_is_member',
			'user_is_segmented',
			'user_transaction_ids',
			'_vwo_*',
			'_vis_*',
			'walkthroughs',
			'spotlight',
		],
		text: {
			accept: t('accept'),
			settings: t('settings'),
			title: t('title'),
			intro: t('intro'),
			acceptSettings: t('accept-settings'),
			rejectSetting: t('reject-settings'),
			notifyTitle: t('notify-title'),
			notifyDescription: t('notify-description'),
			necessaryTitle: t('necessary-title'),
			necessaryDescription: t('necessary-description'),
			on: t('on'),
			off: t('off'),
			closeLabel: t('close-label'),
		},
		branding: {
			fontFamily: 'BrandonText',
			toggleColor: '#181c21',
			removeAbout: true,
			removeIcon: true,
		},
		locales: [
			{
				locale: 'en_US',
				mode: 'ccpa',
				location: ['US'],
			},
		],
		accessibility: {
			overlay: true,
		},
		optionalCookies: [
			{
				name: 'analytics',
				label: t('analytics-title'),
				description: t('analytics-description'),
				cookies: [
					'_ga',
					'_ga_JRGV409BCW',
					'_ga_UA-2787908-11',
					'_gid',
					'amp_ee6070_www.audionetwork.com',
				],
				onAccept: function () {
					console.log('analytics cookies accepted')
					window.gtag('consent', 'update', {
						analytics_storage: 'granted',
						wait_for_update: 500,
					})
					window.dataLayer.push({
						event: 'analyticsConsentGranted',
						wait_for_update: 500,
					})
					window.amplitude.getInstance().enableTracking()

					const commonAmplitudeProperties = {
						'System Colour Scheme': getColorScheme(),
						'App Colour Scheme': getThemeFromCookie(),
						'Logged In': Boolean(user?.id),
					}

					const commonGAProperties = {
						system_colour_scheme: getColorScheme(),
						app_colour_scheme: getThemeFromCookie(),
						logged_in: Boolean(user?.id),
					}

					if (user?.id) {
						const amplitudeUserProperties = {
							...commonAmplitudeProperties,
							'Service Tier': user?.organisation.serviceTier || null,
							'Primary Segment': user.organisation.primarySegment || null,
							'Sub Segment': user.organisation.subSegment || null,
							'Sales Region': user.organisation.salesRegion || null,
							'Website Account Id': user.organisation.id || null,
							'Website Account Name': user.organisation.name || null,
							'Sound Effects': user?.organisation.hasSfx ?? false,
						}
						const filteredAmplitudeProperties = omitBy(
							amplitudeUserProperties,
							isNil
						)

						window.amplitude
							.getInstance()
							.setUserProperties(filteredAmplitudeProperties)

						const gaUserProperties = {
							...commonGAProperties,
							service_tier: user?.organisation.serviceTier || null,
							primary_segment: user.organisation.primarySegment || null,
							sub_segment: user.organisation.subSegment || null,
							sales_region: user.organisation.salesRegion || null,
							website_account_id: user.organisation.id || null,
							website_account_name: user.organisation.name || null,
							sound_effects: user?.organisation.hasSfx ?? false,
						}

						const filteredGAUserProperties = omitBy(gaUserProperties, isNil)

						window.gtag('set', 'user_properties', filteredGAUserProperties)
					} else {
						window.amplitude
							.getInstance()
							.setUserProperties(commonAmplitudeProperties)

						window.gtag('set', 'user_properties', commonGAProperties)
					}

					// This ensures the tracking only fires when the pop-up or settings menu has
					// been interacted with, since the onAccept hook will fire on every page load
					// after a user has accepted analytics tracking
					if (
						document.getElementById('ccc-notify') !== null ||
						document.getElementById('ccc-content') !== null
					) {
						window.amplitude
							.getInstance()
							.logEvent('Accept Analytics Cookies Clicked', {
								CookieControlMode: window.CookieControl.config().mode,
								CurrentPath: window.location.pathname,
							})
					}

					// Timeout to allow time for the Sprig script to initialise from GTM
					setTimeout(() => {
						if (window.Sprig && user) {
							window.Sprig('setUserId', user.id.toString())
							window.Sprig('setAttributes', {
								ReceiveSurveys: user.receiveSurveys,
								ServiceTier: user?.organisation.serviceTier,
								WorkingArea: getWorkingAreaName(user.workingArea),
							})
						}
					}, 1500)
				},
				onRevoke: function () {
					// This ensures the tracking only fires when the pop-up or settings menu has
					// been interacted with, since the onRevoke hook will fire on every page load
					// after a user has rejected analytics tracking
					if (
						document.getElementById('ccc-notify') !== null ||
						document.getElementById('ccc-content') !== null
					) {
						window.amplitude
							.getInstance()
							.logEvent('Reject Analytics Cookies Clicked', {
								CookieControlMode: window.CookieControl.config().mode,
								CurrentPath: window.location.pathname,
							})
					}

					console.log('analytics cookies revoked')
					window.gtag('consent', 'update', {
						analytics_storage: 'denied',
						wait_for_update: 500,
					})
					window.amplitude.getInstance().setOptOut(true)
				},
			},
			{
				name: 'marketing',
				label: t('marketing-title'),
				description: t('marketing-description'),
				cookies: ['_fbp', '__gads', '_gcl_au'],
				onAccept: function () {
					// This ensures the tracking only fires when the pop-up or settings menu has
					// been interacted with, since the onAccept hook will fire on every page load
					// after a user has accepted marketing tracking
					if (
						document.getElementById('ccc-notify') !== null ||
						document.getElementById('ccc-content') !== null
					) {
						window.amplitude
							.getInstance()
							.logEvent('Accept Marketing Cookies Clicked', {
								CookieControlMode: window.CookieControl.config().mode,
								CurrentPath: window.location.pathname,
							})
					}

					console.log('marketing cookies accepted')
					window.gtag('consent', 'update', {
						ad_storage: 'granted',
						ad_personalization: 'granted',
						ad_user_data: 'granted',
						wait_for_update: 500,
					})
					window.dataLayer.push({
						event: 'marketingConsentGranted',
						wait_for_update: 500,
					})
				},
				onRevoke: function () {
					// This ensures the tracking only fires when the pop-up or settings menu has
					// been interacted with, since the onRevoke hook will fire on every page load
					// after a user has rejected marketing tracking
					if (
						document.getElementById('ccc-notify') !== null ||
						document.getElementById('ccc-content') !== null
					) {
						window.amplitude
							.getInstance()
							.logEvent('Reject Marketing Cookies Clicked', {
								CookieControlMode: window.CookieControl.config().mode,
								CurrentPath: window.location.pathname,
							})
					}

					console.log('marketing cookies revoked')
					window.gtag('consent', 'update', {
						ad_storage: 'denied',
						ad_personalization: 'denied',
						ad_user_data: 'denied',
						wait_for_update: 500,
					})
				},
			},
		],

		position: 'RIGHT',
	}

	return config
}

declare global {
	interface Window {
		// Type in ANW Analytics is any, this needs changing
		gtag: any
		CookieControl: {
			config: () => { mode: string }
			load: (a: unknown) => unknown
			geoTest: (a: unknown, b: unknown, c: (arg: unknown) => void) => void
		}
		Sprig: (a: string, b: Record<string, unknown> | string) => void
		dataLayer: any
		amplitude: any
	}
}
